// ترجمة أسماء الصلاحيات (permissions) القادمة من السيرفر إلى العربية.
// الأسماء ثابتة بصيغة "action-module" مثل: view-users, create-products ...
// نعتمد على قاموس للأفعال + قاموس للوحدات، مع fallback ذكي لأي صلاحية جديدة
// لم تُعرَّف صراحةً، حتى لا تظهر بالإنجليزية أبداً قدر الإمكان.

// ترجمات كاملة لبعض الصلاحيات التي يصعب تفكيكها تلقائياً
const EXACT_LABELS = {
  "view-dashboard": "عرض لوحة التحكم",
  "manage-settings": "إدارة الإعدادات",
  "send-notifications": "إرسال الإشعارات",
  "view-notifications": "عرض الإشعارات",
  "update-privacy": "تحديث سياسة الخصوصية",
  "update-terms": "تحديث الشروط والأحكام",
  "view-sales-reports": "عرض تقارير المبيعات",
  "archive-orders": "أرشفة الطلبات",
  "show-orders": "عرض تفاصيل الطلب",
  "change-status-orders": "تغيير حالة الطلب",
  "change-payment-status-orders": "تغيير حالة الدفع",
  "view-wallets": "عرض المحافظ",
  "view-treasuries": "عرض الخزائن",
};

// الأفعال (action) — مرتبة بحيث تُفحص الأطول أولاً
const ACTIONS = [
  ["change-payment-status", "تغيير حالة الدفع"],
  ["change-status", "تغيير حالة"],
  ["view", "عرض"],
  ["show", "عرض تفاصيل"],
  ["create", "إضافة"],
  ["add", "إضافة"],
  ["edit", "تعديل"],
  ["update", "تحديث"],
  ["delete", "حذف"],
  ["destroy", "حذف"],
  ["archive", "أرشفة"],
  ["restore", "استعادة"],
  ["send", "إرسال"],
  ["manage", "إدارة"],
  ["get", "جلب"],
  ["list", "عرض قائمة"],
];

// الوحدات (module)
const MODULES = {
  users: "المستخدمين",
  admins: "المسؤولين",
  roles: "الصلاحيات",
  cities: "المدن",
  regions: "المناطق",
  contacts: "رسائل التواصل",
  faqs: "الأسئلة الشائعة",
  categories: "الفئات",
  features: "المميزات",
  grades: "الصفوف",
  messages: "الرسائل",
  products: "المنتجات",
  sections: "الأقسام",
  sliders: "السلايدر",
  "slider-texts": "نصوص السلايدر",
  subjects: "المواد الدراسية",
  teachers: "المعلمين",
  "teacher-codes": "أكواد المعلمين",
  orders: "الطلبات",
  coupons: "الكوبونات",
  notifications: "الإشعارات",
  wallets: "المحافظ",
  dashboard: "لوحة التحكم",
  "sales-reports": "تقارير المبيعات",
  settings: "الإعدادات",
  privacy: "سياسة الخصوصية",
  terms: "الشروط والأحكام",
};

/**
 * تُرجِع الاسم العربي لصلاحية معيّنة.
 * @param {string} name اسم الصلاحية بالإنجليزية (slug)
 * @returns {string} الاسم بالعربية، أو الاسم الأصلي إذا تعذّرت الترجمة
 */
// BUG-39 fix: توحيد صيغة اسم الصلاحية.
// يحوّل الصيغ المختلفة القادمة من السيرفر (PascalCase / camelCase / snake_case)
// إلى صيغة موحّدة "action-module" بحروف صغيرة، مثل: AddRegion => add-region
const normalizePermissionName = (name) =>
  name
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2") // PascalCase/camelCase => فصل بشرطة
    .replace(/[_\s]+/g, "-") // snake_case أو مسافات => شرطة
    .toLowerCase();

// يبحث عن ترجمة الوحدة مع دعم المفرد/الجمع
const lookupModule = (moduleKey) =>
  MODULES[moduleKey] ||
  MODULES[`${moduleKey}s`] ||
  (moduleKey.endsWith("s") ? MODULES[moduleKey.slice(0, -1)] : undefined);

export const translatePermission = (name) => {
  if (!name || typeof name !== "string") return name || "";

  // 1) ترجمة مباشرة إن وُجدت (بالاسم الأصلي أو الموحّد)
  if (EXACT_LABELS[name]) return EXACT_LABELS[name];
  const normalized = normalizePermissionName(name);
  if (EXACT_LABELS[normalized]) return EXACT_LABELS[normalized];

  // 2) تفكيك إلى فعل + وحدة
  for (const [actionKey, actionLabel] of ACTIONS) {
    if (normalized === actionKey) return actionLabel;
    if (normalized.startsWith(`${actionKey}-`)) {
      const moduleKey = normalized.slice(actionKey.length + 1);
      const moduleLabel = lookupModule(moduleKey);
      if (moduleLabel) return `${actionLabel} ${moduleLabel}`;
      // فعل معروف لكن الوحدة غير معرّفة: نترجم الفعل ونترك الوحدة كما هي
      return `${actionLabel} ${moduleKey}`;
    }
  }

  // 3) لا يمكن تفكيكها: نُعيد الاسم الأصلي
  return name;
};

export default translatePermission;
